import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { predictionEventDurationsQueryOptions } from '@/services/prediction/eventDurations'
import type { PredictionEvent } from '@/services/prediction/types'

import { formatExpiryCountdown } from '../utils/formatExpiryCountdown'
import { isRecurringEvent } from '../utils/recurring'
import useCountdownTimer from './useCountdownTimer'

interface RecurringEventLabel {
  /** 周期展示名（event-durations API 的 display_name，如 "2 hour"），页头用。 */
  durationLabel?: string
  /** 到期倒计时（"35m 20s"），LIVE 副标题用。 */
  expiryCountdown?: string
  /** ISO 到期时间，倒计时 `<time dateTime>` 属性。 */
  expiredAt?: string
}

/**
 * Port of cdc-web `EventCard/hooks/useRecurringEventLabel.ts`。
 *
 * 差异：cdc-web 用 SWR（useEventDurations），这里换 RQ
 * （`predictionEventDurationsQueryOptions`，走 `/predictions/public` 公开前缀——
 * 匿名访客可访问，private 代理会 401）。
 */
export function useRecurringEventLabel(event: PredictionEvent): RecurringEventLabel {
  const recurring = isRecurringEvent(event)
  const { data: eventDurations } = useQuery({
    ...predictionEventDurationsQueryOptions(event.event_kind),
    enabled: recurring,
  })
  const { duration, isEnabled, isExpired } = useCountdownTimer({
    targetTime: event.expired_at ?? '',
    enabled: recurring && !!event.expired_at,
  })

  return useMemo(() => {
    if (!recurring) {
      return { durationLabel: undefined, expiryCountdown: undefined, expiredAt: undefined }
    }
    const durationLabel = eventDurations?.find(
      item => item.duration === event.duration,
    )?.display_name
    const expiryCountdown = isEnabled && !isExpired ? formatExpiryCountdown(duration) : undefined
    return { durationLabel, expiryCountdown, expiredAt: event.expired_at }
  }, [recurring, eventDurations, event.duration, event.expired_at, isEnabled, isExpired, duration])
}
